import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from "react-router";
import { AppErrorBoundary } from "./error-boundary";
import { AppShell } from "./app-shell";
import { RouteErrorView } from "./route-error-view";
import { WorkspaceLayoutActionsProvider } from "./shell/workspace-layout-actions";
import { LoadingState } from "../ui/loading-state";

export const routes: RouteObject[] = [
  {
    element: (
      <AppErrorBoundary>
        <WorkspaceLayoutActionsProvider>
          <AppShell />
        </WorkspaceLayoutActionsProvider>
      </AppErrorBoundary>
    ),
    errorElement: <RouteErrorView />,
    hydrateFallbackElement: <LoadingState label="Loading operational workspace…" />,
    children: [
      {
        // ADR-UI-004: the main screen is the non-scrolling global FlexLayout hub.
        // The former home dashboard is now the `route-home` panel inside it.
        index: true,
        lazy: () => import("./routes/hub-route"),
      },
      {
        path: "system",
        element: <Navigate replace to="/" />,
      },
      {
        path: "workspace",
        // The former parallel workspace exposed a second, partially populated
        // layout system. Keep old bookmarks valid while routing all users into
        // the canonical global FlexLayout shell.
        element: <Navigate replace to="/" />,
      },
      {
        path: "operations",
        lazy: () => import("./routes/operations-route"),
      },
      {
        path: "requirements",
        lazy: () => import("./routes/requirements-route"),
      },
      {
        // The Schirrmeister/quartermaster entry point. Same workspace module as
        // `/operations` (the route reads `location.pathname` generically and
        // never hard-codes its own path), mounted under its own URL so the
        // LOGISTICS perspective is addressable, bookmarkable and independently
        // highlightable in the header instead of being a second link into
        // `/operations` that could never light up. The layout itself still
        // comes from the active work context's role perspective, so no other
        // role is affected by this path existing.
        path: "logistics",
        lazy: () => import("./routes/operations-route"),
      },
      {
        path: "hub",
        lazy: () => import("./routes/hub-route"),
      },
      {
        path: "workspace/popout/:panelId",
        lazy: () => import("./routes/popout-route"),
      },
      {
        path: "modules",
        lazy: () => import("./routes/modules-route"),
      },
      {
        path: "modules/:featureId",
        lazy: () => import("./routes/feature-route"),
      },
      {
        path: "administration",
        lazy: () => import("./routes/administration-route"),
      },
      {
        path: "audit",
        element: <Navigate replace to="/administration?section=audit" />,
      },
      {
        path: "offline",
        element: <Navigate replace to="/" />,
      },
      {
        path: "incidents",
        // Preserve old bookmarks without mounting the retired private
        // FlexLayout island. The global workspace owns the incident panels
        // and supplies the established FlexLayout-free mobile fallback.
        element: <Navigate replace to="/?open=incidents" />,
      },
      {
        path: "mission",
        lazy: () => import("./routes/mission-route"),
      },
      {
        // Design-review surface only: the redesign prototype (storyboard, flood
        // scenario, Level 2 workbenches). Not linked from the shell navigation —
        // it renders its own chrome and is reached by URL while the redesign is
        // being ported into the real features.
        path: import.meta.env.DEV ? "ui-redesign" : "__disabled-ui-redesign",
        ...(import.meta.env.DEV
          ? { lazy: () => import("../features/ui-redesign/ui-redesign-route") }
          : {}),
      },
    ],
  },
  {
    // Design-review surface only: the A/B/C comparison between the current Home
    // and the design systems generated by the `ui-ux-pro-max` skill.
    //
    // Deliberately mounted OUTSIDE `AppShell`, unlike `/ui-redesign`: the lab is
    // fixture-backed and reads no store, so the shell header would only steal
    // viewport height from the comparison. It is still behind authentication —
    // `SessionGate` wraps the whole `RouterProvider` in `AppProviders`, above
    // every route — and this route deliberately does not change that. Never
    // linked from navigation; safe to delete together with its feature folder
    // once the experiment's conclusions have been folded into the real Home.
    errorElement: <RouteErrorView />,
    hydrateFallbackElement: <LoadingState label="Loading design lab…" />,
    ...(import.meta.env.DEV
      ? { lazy: () => import("../features/design-lab/design-lab-route") }
      : {}),
    path: import.meta.env.DEV ? "design-lab" : "__disabled-design-lab",
  },
];

export const router = createBrowserRouter(routes);
